import { createSlice } from '@reduxjs/toolkit';

// Billing — the commercial layer (entitlements drive what an org is allowed to use).
// Holds the current org's billing state (plan, entitlements, license-key PREFIX, renewal).
// Reloaded on org (re)select. Entitlement is the source of truth the UI gates on; the module
// flag (Settings) and integration config remain separate gates.
const initialState = {
	state: null, // { orgID, plan, subscription, renewalDate, entitlements[], items[], licenseKey:{prefix,status} }
	// The org `state` was loaded FOR (stamped by the saga from the request, not trusted from the body).
	// Selectors refuse billing data whose org is not the selected one — see selectBillingState.
	orgID: null,
	loadLoading: false,
	error: null,
	// A refused read's published code, and the org it was refused FOR (stamped by the saga from the request). A refusal
	// belongs to that org only: selectBillingAccessEnded ignores it for any other org, and a new read clears it.
	errorCode: null,
	errorOrgID: null,

	// Internal-admin actions
	dealLoading: false,
	lastDealResult: null, // { created, licenseKey:{ plaintext? , prefix, status }, ... } — plaintext shown once
	overrideLoading: false,

	// Customer applies an offline-minted licence key
	applyLoading: false,

	// Activation-key preview (preview → confirm flow): what the key WOULD activate, no commit.
	preview: { loading: false, data: null, error: null },

	// Online card checkout (Stripe)
	checkout: {
		loading: false,
		clientSecret: null,
		status: null // INCOMPLETE → (webhook) ACTIVE
	},

	// Site-trial customer actions (B20): cancel / resume at period end, card update. One at a time.
	// `action` is the one in flight ('cancel' | 'resume' | 'card-update'); `lastResult` the server's answer.
	siteTrialAction: { action: null, loading: false, lastResult: null, error: null },

	// v1.15 customer licensing read (GET /billing/licensing; App Server AMENDMENTS.v1.15). `status`:
	//   'idle' | 'loading' | 'loaded' (200, `data` is the read) | 'hidden' (404: flag off / no relationship — the legacy
	//   path) | 'unavailable' (any other failure — "Not confirmed"). Never read as "not licensed" unless loaded.
	// `orgID` is the org the read was made FOR (stamped by the saga); selectors refuse another org's read.
	licensing: { status: 'idle', data: null, orgID: null }
};

// One idempotency key per user action. Carried on the action, so a retry of the same click reuses it.
const newOperationID = () => {
	const c = typeof globalThis !== 'undefined' ? globalThis.crypto : undefined;
	if (c?.randomUUID) return c.randomUUID();
	return `op-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

const billingSlice = createSlice({
	name: 'billing',
	initialState,
	reducers: {
		loadBillingState: (state) => {
			state.loadLoading = true;
			state.error = null;
			// a new read starts without the last refusal: it shows loading, then this read's own answer
			state.errorCode = null;
			state.errorOrgID = null;
		},
		// loadBillingStateSuccess(data, orgID) — orgID is the org the request was made for.
		loadBillingStateSuccess: {
			reducer: (state, action) => {
				state.loadLoading = false;
				state.errorCode = null;
				state.errorOrgID = null;
				state.state = action.payload;
				state.orgID = action.meta?.orgID ?? action.payload?.orgID ?? null;
			},
			prepare: (data, orgID) => ({ payload: data, meta: { orgID: orgID ?? null } })
		},
		// loadBillingStateError(message) or loadBillingStateError({ message, errorCode }, orgID) — orgID is the org the
		// refused request was made for.
		loadBillingStateError: {
			reducer: (state, action) => {
				const p = action.payload;
				state.loadLoading = false;
				state.error = p && typeof p === 'object' ? (p.message ?? null) : p;
				state.errorCode = (p && typeof p === 'object' && p.errorCode) || null;
				state.errorOrgID = action.meta?.orgID ?? null;
			},
			prepare: (error, orgID) => ({ payload: error, meta: { orgID: orgID ?? null } })
		},

		// Internal-admin: build + activate a manual deal. Success payload may carry the
		// license-key plaintext ONCE (created=true) — never persisted, shown once in the UI.
		activateDeal: (state) => {
			state.dealLoading = true;
			state.error = null;
			state.lastDealResult = null;
		},
		activateDealSuccess: (state, action) => {
			state.dealLoading = false;
			state.lastDealResult = action.payload;
		},
		activateDealError: (state, action) => {
			state.dealLoading = false;
			state.error = action.payload;
		},
		clearDealResult: (state) => {
			state.lastDealResult = null;
		},

		// Internal-admin: manual entitlement grant / revoke (audited server-side).
		grantEntitlement: (state) => {
			state.overrideLoading = true;
			state.error = null;
		},
		grantEntitlementSuccess: (state) => {
			state.overrideLoading = false;
		},
		grantEntitlementError: (state, action) => {
			state.overrideLoading = false;
			state.error = action.payload;
		},
		revokeEntitlement: (state) => {
			state.overrideLoading = true;
			state.error = null;
		},
		revokeEntitlementSuccess: (state) => {
			state.overrideLoading = false;
		},
		revokeEntitlementError: (state, action) => {
			state.overrideLoading = false;
			state.error = action.payload;
		},

		// Customer applies an offline-minted licence key to activate entitlements.
		applyLicenseKey: (state) => {
			state.applyLoading = true;
			state.error = null;
		},
		applyLicenseKeySuccess: (state) => {
			state.applyLoading = false;
		},
		applyLicenseKeyError: (state, action) => {
			state.applyLoading = false;
			state.error = action.payload;
		},

		// Preview an activation key before confirming — never commits (preview → confirm wizard).
		previewLicenseKey: (state) => {
			state.preview.loading = true;
			state.preview.error = null;
			state.preview.data = null;
		},
		previewLicenseKeySuccess: (state, action) => {
			state.preview.loading = false;
			state.preview.data = action.payload;
		},
		previewLicenseKeyError: (state, action) => {
			state.preview.loading = false;
			state.preview.error = action.payload;
		},
		clearPreview: (state) => {
			state.preview = { loading: false, data: null, error: null };
		},

		// Online card checkout (Stripe): start a subscription → get a clientSecret to confirm a card.
		startStripeSubscription: (state) => {
			state.checkout.loading = true;
			state.checkout.clientSecret = null;
			state.error = null;
		},
		startStripeSubscriptionSuccess: (state, action) => {
			state.checkout.loading = false;
			state.checkout.clientSecret = action.payload?.clientSecret || null;
			state.checkout.status = action.payload?.status || null;
		},
		startStripeSubscriptionError: (state, action) => {
			state.checkout.loading = false;
			state.error = action.payload;
		},
		resetCheckout: (state) => {
			state.checkout = { loading: false, clientSecret: null, status: null };
		},

		// Site-trial (B20). requestSiteTrialAction({ action }) — action: 'cancel' | 'resume' | 'card-update'.
		// The server decides what is allowed (it returns `agreement.actions`); the UI only offers those.
		requestSiteTrialAction: {
			reducer: (state, action) => {
				state.siteTrialAction = { action: action.payload.action, loading: true, lastResult: null, error: null };
			},
			prepare: ({ action, operationID } = {}) => ({
				payload: { action, operationID: operationID || newOperationID() }
			})
		},
		requestSiteTrialActionSuccess: (state, action) => {
			state.siteTrialAction.loading = false;
			state.siteTrialAction.lastResult = action.payload;
		},
		requestSiteTrialActionError: (state, action) => {
			state.siteTrialAction.loading = false;
			state.siteTrialAction.error = action.payload;
		},

		// v1.15 customer licensing read. Each outcome is stamped with the org it was read for.
		loadCustomerLicensing: (state) => {
			state.licensing = { status: 'loading', data: null, orgID: null };
		},
		loadCustomerLicensingSuccess: {
			reducer: (state, action) => {
				state.licensing = { status: 'loaded', data: action.payload ?? null, orgID: action.meta?.orgID ?? null };
			},
			prepare: (data, orgID) => ({ payload: data, meta: { orgID: orgID ?? null } })
		},
		loadCustomerLicensingHidden: {
			reducer: (state, action) => {
				state.licensing = { status: 'hidden', data: null, orgID: action.meta?.orgID ?? null };
			},
			prepare: (orgID) => ({ payload: null, meta: { orgID: orgID ?? null } })
		},
		loadCustomerLicensingUnavailable: {
			reducer: (state, action) => {
				state.licensing = { status: 'unavailable', data: null, orgID: action.meta?.orgID ?? null };
			},
			prepare: (orgID) => ({ payload: null, meta: { orgID: orgID ?? null } })
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => initialState);
		// 🛑 Billing is ORG-SCOPED: an org switch must drop the previous org's entitlements, or the
		// gating selectors judge the new org by the old org's billing (hiding its modules until reload).
		// Empty state = gating inactive = grandfather-safe until the new org's billing loads
		// (auth/switchProfileSuccess → settings/loadOrgSettings → billing saga).
		builder.addCase('auth/switchProfileSuccess', () => initialState);
	}
});

export const billingActions = billingSlice.actions;
export default billingSlice.reducer;
